/**
 * CountryFlag — inline SVG country flag badge.
 *
 * Emoji flags are not usable here: Windows ships no glyphs for regional
 * indicator pairs, so Chrome and Edge render '🇺🇸' as the bare letters "US".
 * These SVGs render identically on every platform.
 *
 * Decorative — every caller states the jurisdiction in adjacent text
 * (a card bullet or the profile subtitle), so the flag is aria-hidden.
 */

/** Star grid for the US canton — 5 columns x 4 rows reads cleanly down to 16px. */
const US_STAR_COLUMNS = [0, 1, 2, 3, 4];
const US_STAR_ROWS = [0, 1, 2, 3];

function UnitedStates() {
  return (
    <>
      <rect width="19" height="10" fill="#FFFFFF" />
      {[0, 2, 4, 6, 8, 10, 12].map(stripe => (
        <rect key={stripe} y={(stripe * 10) / 13} width="19" height={10 / 13} fill="#B22234" />
      ))}
      <rect width="7.6" height={(5 * 10) / 13} fill="#3C3B6E" />
      {US_STAR_ROWS.map(row => US_STAR_COLUMNS.map(column => <circle key={`${row}-${column}`} cx={0.76 + column * 1.52} cy={0.48 + row * 0.96} r="0.24" fill="#FFFFFF" />))}
    </>
  );
}

function China() {
  return (
    <>
      <rect width="19" height="10" fill="#DE2910" />
      <polygon points="4,1 4.47,2.35 5.9,2.35 4.74,3.18 5.18,4.55 4,3.72 2.82,4.55 3.26,3.18 2.1,2.35 3.53,2.35" fill="#FFDE00" />
      <polygon points="7.7,0.9 8.05,1.85 9.05,1.88 8.25,2.47 8.55,3.43 7.7,2.86 6.85,3.43 7.15,2.47 6.35,1.88 7.35,1.85" fill="#FFDE00" />
      <polygon points="9.05,3.1 9.4,4.05 10.4,4.08 9.6,4.67 9.9,5.63 9.05,5.06 8.2,5.63 8.5,4.67 7.7,4.08 8.7,4.05" fill="#FFDE00" />
      <polygon points="8.15,5.55 8.5,6.5 9.5,6.53 8.7,7.12 9,8.08 8.15,7.51 7.3,8.08 7.6,7.12 6.8,6.53 7.8,6.5" fill="#FFDE00" />
      <polygon points="6.4,7.15 6.75,8.1 7.75,8.13 6.95,8.72 7.25,9.68 6.4,9.11 5.55,9.68 5.85,8.72 5.05,8.13 6.05,8.1" fill="#FFDE00" />
    </>
  );
}

function Vietnam() {
  return (
    <>
      <rect width="19" height="10" fill="#DA251D" />
      <polygon points="9.5,1.15 10.45,4 13.45,4 11.02,5.75 11.95,8.6 9.5,6.85 7.05,8.6 7.98,5.75 5.55,4 8.55,4" fill="#FFCD00" />
    </>
  );
}

/**
 * Korea — official 3:2 construction: the taegeuk roundel is a quarter of the
 * flag height in radius and the four trigrams sit on the ±33.69° diagonals.
 * Drawn from the national geometry, then uniformly scaled into the shared
 * 19x10 badge box so the roundel stays circular instead of being squashed.
 */
function Korea() {
  return (
    <>
      <rect width="19" height="10" fill="#FFFFFF" />
      <g transform="translate(9.5 5) scale(0.10416667)">
        <g fill="none" stroke="#0F1419" strokeWidth="4">
          <path transform="rotate(33.69006752598)" d="M-50-12v24m6 0v-24m6 0v24m76 0V1m0-2v-11m6 0v11m0 2v11m6 0V1m0-2v-11" />
          <path transform="rotate(-33.69006752598)" d="M-50-12v24m6 0V1m0-2v-11m6 0v24m76 0V1m0-2v-11m6 0v24m6 0V1m0-2v-11" />
        </g>
        <g transform="rotate(33.69006752598)">
          <path fill="#CD2E3A" d="M12 0a18 18 0 11-36 0 24 24 0 1148 0" />
          <path fill="#0047A0" d="M-24 0a24 24 0 1048 0A12 12 0 100 0a12 12 0 11-24 0" />
        </g>
      </g>
    </>
  );
}

/** Japan — white field, red disc of 3/5 the flag height, centred. */
function Japan() {
  return (
    <>
      <rect width="19" height="10" fill="#FFFFFF" />
      <circle cx="9.5" cy="5" r="3" fill="#BC002D" />
    </>
  );
}

const FLAGS: Record<string, () => JSX.Element> = {
  US: UnitedStates,
  CN: China,
  VN: Vietnam,
  KR: Korea,
  JP: Japan,
};

/** Codes this component can draw. Data files are checked against it in tests. */
export const COUNTRY_FLAG_CODES = Object.keys(FLAGS);

interface CountryFlagProps {
  /** ISO 3166-1 alpha-2 country code, e.g. 'US'. Unknown codes render nothing. */
  code: string;
  /** Sizing/spacing classes from the caller. */
  className?: string;
}

export default function CountryFlag({ code, className }: CountryFlagProps) {
  const Flag = FLAGS[code];
  if (!Flag) return null;

  return (
    <svg viewBox="0 0 19 10" role="presentation" aria-hidden="true" focusable="false" className={`inline-block rounded-[1px] ring-1 ring-inset ring-lawkitGray-300/70 ${className ?? ''}`}>
      <Flag />
    </svg>
  );
}
